import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { StatusBadge } from '@/components/StatusBadge'
import { EmptyState } from '@/components/EmptyState'
import { AlertTriangle, Plus, Mail, MoreHorizontal, Play, Pause, Trash2 } from 'lucide-react'
import { useSequences, useCreateSequence, useDeleteSequence, usePauseSequence } from '@/hooks/useApi'
import { useActivateSequenceGuarded } from '@/hooks/useActivateSequenceGuarded'
import { isSendBlocked } from '@/lib/sequences'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

export function Sequences() {
  const { t } = useTranslation(['sequences', 'common'])
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')

  const { data: sequences, isLoading } = useSequences()
  const createSequence = useCreateSequence()
  const deleteSequence = useDeleteSequence()
  const { activate } = useActivateSequenceGuarded()
  const pauseSequence = usePauseSequence()

  const handleCreate = () => {
    if (!name.trim()) return
    createSequence.mutate(
      { name, description: description || undefined },
      {
        onSuccess: () => {
          setOpen(false)
          setName('')
          setDescription('')
        },
      }
    )
  }

  const handleDelete = (id: string) => {
    if (confirm(t('list.deleteConfirm'))) {
      deleteSequence.mutate(id)
    }
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold">{t('list.title')}</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              {t('list.new')}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('list.create')}</DialogTitle>
              <DialogDescription>
                {t('list.createDescription')}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">{t('fields.name')}</Label>
                <Input
                  id="name"
                  placeholder={t('list.namePlaceholder')}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">{t('list.descriptionOptional')}</Label>
                <Textarea
                  id="description"
                  placeholder={t('list.descriptionPlaceholder')}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>
                {t('common:cancel')}
              </Button>
              <Button onClick={handleCreate} disabled={!name.trim() || createSequence.isPending}>
                {createSequence.isPending ? t('list.creating') : t('list.createSequence')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('list.yourSequences')}</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : sequences && sequences.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('list.table.name')}</TableHead>
                  <TableHead>{t('list.table.status')}</TableHead>
                  <TableHead className="text-center">{t('list.table.steps')}</TableHead>
                  <TableHead className="text-center">{t('list.table.enrollments')}</TableHead>
                  <TableHead className="text-right">{t('list.table.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sequences.map((sequence) => (
                  <TableRow key={sequence.id}>
                    <TableCell>
                      <Link
                        to={`/sequences/${sequence.id}`}
                        className="font-medium hover:underline"
                      >
                        {sequence.name}
                      </Link>
                      {sequence.description && (
                        <p className="text-sm text-muted-foreground truncate max-w-xs">
                          {sequence.description}
                        </p>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <StatusBadge status={sequence.status} />
                        {isSendBlocked(sequence) && (
                          <Link
                            to={`/sequences/${sequence.id}`}
                            className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800 hover:bg-amber-100"
                          >
                            <AlertTriangle className="h-3 w-3" />
                            {t('noSender.badge')}
                          </Link>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      {sequence.steps_count ?? 0}
                    </TableCell>
                    <TableCell className="text-center">
                      {sequence.enrollments_count ?? 0}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {sequence.status === 'active' ? (
                            <DropdownMenuItem
                              onClick={() => pauseSequence.mutate(sequence.id)}
                            >
                              <Pause className="h-4 w-4 mr-2" />
                              {t('list.pause')}
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem
                              onClick={() => activate(sequence)}
                            >
                              <Play className="h-4 w-4 mr-2" />
                              {t('list.activate')}
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            onClick={() => handleDelete(sequence.id)}
                            className="text-red-600"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            {t('common:delete')}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <EmptyState
              icon={Mail}
              title={t('list.empty.title')}
              description={t('list.empty.description')}
              action={{
                label: t('list.createSequence'),
                onClick: () => setOpen(true),
              }}
            />
          )}
        </CardContent>
      </Card>
    </div>
  )
}
